import { useEffect, useRef, useState } from 'react'
import { Icon } from '@iconify/react'
import { ApiClientError } from '@/lib/api/client'
import { importWithRetry, isModuleLoadError } from '@/lib/module-load'
import {
  ParticipantShareDialog,
  type PreparedParticipantShare,
} from './ParticipantShareDialog'
import {
  buildParticipantShareModel,
  fetchParticipantShareSnapshot,
  type ParticipantShareInput,
} from './participant-share'
import styles from './ParticipantShareButton.module.css'

export function ParticipantShareButton({
  campaign,
  institution,
  officialShareUrl,
  onShareAction,
  label = 'Compartilhar minha jornada',
  description = 'Seu avatar, suas conquistas, seu convite.',
  emphasized = false,
}: Omit<ParticipantShareInput, 'origin'> & {
  onShareAction?: () => void
  label?: string
  description?: string
  emphasized?: boolean
}) {
  const [open, setOpen] = useState(false)
  const [prepared, setPrepared] = useState<PreparedParticipantShare | null>(null)
  const [error, setError] = useState<string | null>(null)
  const active = useRef<AbortController | null>(null)
  const previewUrl = useRef<string | null>(null)

  const release = () => {
    active.current?.abort()
    active.current = null
    if (previewUrl.current) URL.revokeObjectURL(previewUrl.current)
    previewUrl.current = null
  }
  useEffect(() => release, [])

  const prepare = async () => {
    if (active.current) return
    setOpen(true)
    setError(null)
    setPrepared(null)
    const controller = new AbortController()
    active.current = controller
    let timedOut = false
    const timeout = window.setTimeout(() => {
      timedOut = true
      controller.abort()
      if (active.current === controller) {
        active.current = null
        setError('A preparação demorou mais que o esperado. Tente novamente.')
      }
    }, 45000)
    try {
      const snapshot = await fetchParticipantShareSnapshot(campaign.id, controller.signal)
      controller.signal.throwIfAborted()
      const model = buildParticipantShareModel(
        { campaign, institution, origin: window.location.origin, officialShareUrl },
        snapshot
      )
      const { renderParticipantShareCard } = await importWithRetry(
        () => import('./renderParticipantShareCard')
      )
      const blob = await renderParticipantShareCard(model, controller.signal)
      controller.signal.throwIfAborted()
      const file = new File([blob], model.filename, { type: 'image/png' })
      const url = URL.createObjectURL(blob)
      previewUrl.current = url
      setPrepared({ model, file, previewUrl: url })
    } catch (err) {
      if (!controller.signal.aborted && !timedOut)
        setError(
          err instanceof ApiClientError && err.status === 401
            ? 'Sua sessão expirou. Entre novamente para criar seu card.'
            : isModuleLoadError(err)
              ? 'Não foi possível carregar o criador de cards. Confira sua conexão. Se o erro continuar, salve suas alterações e recarregue a página.'
              : 'Não foi possível preparar seu card. Confira sua conexão e tente novamente.'
        )
    } finally {
      window.clearTimeout(timeout)
      if (active.current === controller) active.current = null
    }
  }
  const close = () => {
    release()
    setOpen(false)
    setPrepared(null)
  }

  return (
    <>
      <button
        type="button"
        className={`${styles.button} ${emphasized ? styles.buttonEmphasized : ''}`}
        onClick={() => void prepare()}
        aria-haspopup="dialog"
      >
        <Icon icon="mdi:share-variant-outline" />
        <span>
          {label}
          <small>{description}</small>
        </span>
        <Icon icon="mdi:arrow-top-right" />
      </button>
      {open && (
        <ParticipantShareDialog
          prepared={prepared}
          error={error}
          onClose={close}
          onRetry={() => void prepare()}
          onShareAction={onShareAction}
        />
      )}
    </>
  )
}
